import { useState } from 'react';
import { Panel } from '../whoop-panel';
import { BaselineBars, Spark, gen } from '../whoop-charts';
import { C } from '../whoop-theme';

const RANGES = ['7d', '30d', '90d', '6m', '1y'];
const RANGE_LABEL: Record<string, string> = {
  '7d': 'last 7 days',
  '30d': 'last 30 days',
  '90d': 'last 90 days',
  '6m': 'last 6 months',
  '1y': 'last year',
};

const TIMELINE = [
  {
    label: 'Recovery',
    value: '72%',
    delta: '30-day avg 74%',
    series: gen(30, 72, 12, -0.1),
    color: C.accent,
    baseline: 74,
  },
  {
    label: 'HRV',
    value: '58 ms',
    delta: '−10% vs baseline',
    series: gen(30, 63, 5, -0.2),
    color: C.accent,
    baseline: 64,
  },
  {
    label: 'Resting HR',
    value: '53 bpm',
    delta: '+6% vs baseline',
    series: gen(30, 49, 2.5, 0.13),
    color: C.low,
    baseline: 49,
  },
  {
    label: 'Sleep',
    value: '7h 06m',
    delta: 'near target',
    series: gen(30, 7.1, 0.7, 0),
    color: C.accent,
    baseline: 7.5,
  },
  {
    label: 'Strain',
    value: '12.8 avg',
    delta: '+9% this week',
    series: gen(30, 11, 3.5, 0.05),
    color: C.good,
    baseline: null,
  },
];

const RESPONSE = [
  'Your strongest quality workouts follow two consecutive nights above your sleep target.',
  'Recovery reliably dips after three straight high-strain days.',
  'Threshold sessions stay sustainable when separated by at least two easier days.',
  'Your best recovery weeks always include one full rest day.',
];

const CONSISTENCY = [
  { k: 'Runs completed', v: '92%', sub: '46 of 50 planned' },
  { k: 'Hard workouts hit', v: '78%', sub: '14 of 18 planned' },
  { k: 'Avg weekly runs', v: '4.6', sub: 'last 8 weeks' },
  { k: 'Workout skip rate', v: '8%', sub: 'down from 19%' },
  { k: 'Adaptation acceptance', v: '71%', sub: 'of suggestions used' },
  { k: 'Longest run', v: '16 mi', sub: 'trending up' },
];

const YEAR = [
  { k: 'Total workouts', v: '214' },
  { k: 'Training hours', v: '186 h' },
  { k: 'Hardest month', v: 'March' },
  { k: 'Most consistent', v: 'May' },
  { k: 'Longest streak', v: '23 days' },
  { k: 'Best recovery streak', v: '9 days' },
  { k: 'Biggest block', v: '42 mi week' },
  { k: 'Top workout type', v: 'Easy run' },
];

export function TrendsPage() {
  const [range, setRange] = useState('30d');

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', margin: 0 }}>
            Trends
          </h1>
          <div style={{ fontSize: 14, color: C.text2, marginTop: 4 }}>
            Patterns in my body and training over time — {RANGE_LABEL[range]}.
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 3,
            background: 'rgba(255,255,255,0.04)',
            borderRadius: 9,
            padding: 3,
          }}
        >
          {RANGES.map((r) => {
            const active = r === range;
            return (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 11px',
                  background: active ? C.accent : 'transparent',
                  color: active ? '#1a1330' : C.text2,
                }}
              >
                {r}
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <Panel style={{ padding: '22px 24px', marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Performance timeline</div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {TIMELINE.map((row) => (
            <div
              key={row.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 18,
                padding: '13px 0',
                borderTop: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <div style={{ width: 110, flex: 'none' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 700 }}>{row.label}</div>
                <div style={{ fontSize: 11, color: C.text2, marginTop: 2 }}>{row.delta}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>
                <Spark series={row.series} color={row.color} baseline={row.baseline} />
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: "'JetBrains Mono',monospace",
                  minWidth: 84,
                  textAlign: 'right',
                }}
              >
                {row.value}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Baseline + response */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
        <Panel style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Baseline deviation</div>
          <div style={{ fontSize: '11.5px', color: C.text3, marginBottom: 18 }}>
            vs. 30-day personal baseline
          </div>
          <BaselineBars />
        </Panel>
        <Panel style={{ padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
            Training response patterns
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {RESPONSE.map((rp) => (
              <div
                key={rp}
                style={{
                  display: 'flex',
                  gap: 11,
                  alignItems: 'flex-start',
                  fontSize: 13,
                  color: '#cbc9c2',
                  lineHeight: 1.45,
                }}
              >
                <span style={{ color: C.accent, marginTop: 2 }}>›</span>
                {rp}
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Consistency */}
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Consistency</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(168px,1fr))',
          gap: 12,
          marginBottom: 28,
        }}
      >
        {CONSISTENCY.map((cm) => (
          <div
            key={cm.k}
            style={{
              border: '1px solid rgba(255,255,255,0.08)',
              background: C.panel,
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div
              style={{
                fontSize: 24,
                fontWeight: 800,
                fontFamily: "'JetBrains Mono',monospace",
                letterSpacing: '-0.02em',
              }}
            >
              {cm.v}
            </div>
            <div style={{ fontSize: '12.5px', fontWeight: 600, marginTop: 6 }}>{cm.k}</div>
            <div style={{ fontSize: '11.5px', color: C.text3, marginTop: 2 }}>{cm.sub}</div>
          </div>
        ))}
      </div>

      {/* Year in training */}
      <div
        style={{
          border: '1px solid rgba(181,155,255,0.22)',
          background: 'linear-gradient(180deg,rgba(181,155,255,0.07),rgba(20,20,22,0.3))',
          borderRadius: 18,
          padding: 26,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: 10,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: C.accent,
              border: '1px solid rgba(181,155,255,0.4)',
              borderRadius: 6,
              padding: '4px 9px',
            }}
          >
            Year in training
          </span>
          <span style={{ fontSize: 13, color: C.text2 }}>2025–26 · private recap</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))',
            gap: 14,
          }}
        >
          {YEAR.map((y) => (
            <div key={y.k}>
              <div style={{ fontSize: '11.5px', color: C.text2, marginBottom: 5 }}>{y.k}</div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>{y.v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
